import React, { useCallback, useEffect } from 'react'

import { ButtonV2 } from '@labrav/react-components'

import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { ProductType } from '../../../types'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { baseURL } from '../../../utils/constants'
import { errorLogger } from '../../../utils/ErrorHandler'
import {
  closeSignInWindow,
  openSignInWindow,
} from '../../../utils/signInWindow'
import {
  getAzureAccountDetails,
  initiateAzureOAuthFlow,
  sendAzureAccountRegistration,
} from '../cloudSettingsAccountActions'
import { useOnboardingAccountId } from '../../../utils/hooks/useCrmOnboardingContext'
import { PartnerType } from '../../../types/partner'
import { updateAppAlert } from '../../../modules/appAlert/actions'
import type { CloudSettingsState } from '../cloudSettingsState'
import {
  ADMIN_CONSENT_REQUIRED_MESSAGE,
  buildAzureOauthReturnPath,
  clearAzureOauthAuthUri,
  clearAzureOauthSession,
  completeAzureOauthCallback,
  getAzureOauthAccountId,
  getAzureOauthAuthUri,
  parseAzureOauthCallbackParams,
  pickConsentOrAuthUri,
  releaseAzureOauthCallback,
  resolveAdminConsentCallback,
  setAzureOauthAccountId,
  setAzureOauthAuthUri,
  tryClaimAzureOauthCallback,
} from '../azureOauth'

type AzureRegistrationResult = {
  alreadyConnected?: boolean
  error?: boolean
}

export const AzurePopupConnection: React.FC<{
  productType: ProductType
  partnerType?: PartnerType
  enabled: boolean
}> = ({ productType, enabled }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch() as (action: unknown) => Promise<any>
  const { pathname, search: queryString } = useLocation()
  const { accountId: accountIdFromParams } = useParams<{ accountId?: string }>()
  const accountIdFromHook = useOnboardingAccountId()
  const accountId = accountIdFromParams || accountIdFromHook
  const searchParams = new URLSearchParams(queryString)
  const code = searchParams.get('code') || ''
  const state = searchParams.get('state') || ''
  const sessionState = searchParams.get('session_state') || ''
  const adminConsent = searchParams.get('admin_consent') || ''
  const consentError = searchParams.get('error') || ''
  const consentErrorDescription = searchParams.get('error_description') || ''

  const isOnSettingsPage = !!accountIdFromParams
  const authUri = useSelector((state: CloudSettingsState) => {
    if (isOnSettingsPage) {
      return (
        state.accountForm[productType].selectedAccountDetails?.AZURE?.authUri ||
        ''
      )
    }
    return state.accountForm[productType].accountDetails?.AZURE?.authUri || ''
  })

  const submitAzureConnection = useCallback(
    async (code: string, state: string, sessionState: string) => {
      if (!accountId) {
        return { error: true as const }
      }
      const result = (await dispatch(
        sendAzureAccountRegistration(
          productType,
          code,
          state,
          sessionState,
          accountId
        )
      )) as AzureRegistrationResult | undefined

      if (result?.error) {
        return { error: true as const }
      }

      await dispatch(
        getAzureAccountDetails(accountId, productType, !!accountIdFromParams)
      )
      navigate(window.location.pathname, { replace: true })
      return { error: false as const }
    },
    [dispatch, navigate, accountId, productType, accountIdFromParams]
  )

  const receiveMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== baseURL) {
        console.error('Event is coming from unauthorized source.')
        const e: Error = {
          name: 'Unauthorized origin',
          message: 'Event is coming from unauthorized source.',
        }
        errorLogger(e as Error, {
          message: 'Event is coming from unauthorized source.',
        })
        return
      }
      const { data } = event
      if (
        data.source === 'callback' &&
        data.params !== undefined &&
        !isEmpty(data.params)
      ) {
        const callbackParams = parseAzureOauthCallbackParams(data.params)
        const adminConsentResult = resolveAdminConsentCallback(
          callbackParams,
          authUri
        )

        if (adminConsentResult.status !== 'not_admin_consent') {
          if (adminConsentResult.status === 'continue') {
            openSignInWindow(
              'AZURE',
              adminConsentResult.authUri,
              receiveMessage
            )
            return
          }

          clearAzureOauthSession()
          dispatch(
            updateAppAlert({
              message: adminConsentResult.message,
              messageType: 'ERROR',
              autoClose: true,
            })
          )
          closeSignInWindow()
          return
        }

        const effectiveAccountId =
          getAzureOauthAccountId() || accountId || undefined
        navigate(
          buildAzureOauthReturnPath({
            productType,
            accountId: effectiveAccountId,
            queryString: callbackParams.toString(),
          }),
          { replace: true }
        )
      }
    },
    [navigate, productType, accountId, authUri, dispatch]
  )

  useEffect(() => {
    window.addEventListener('message', receiveMessage)
    return () => {
      window.removeEventListener('message', receiveMessage)
    }
  }, [receiveMessage])

  useEffect(() => {
    if (!enabled || !accountId) {
      return
    }

    const clearQuery = () => navigate(pathname, { replace: true })

    if (!isEmpty(consentError)) {
      clearAzureOauthSession()
      dispatch(
        updateAppAlert({
          message: consentErrorDescription || ADMIN_CONSENT_REQUIRED_MESSAGE,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      clearQuery()
      return
    }

    if (adminConsent.toLowerCase() === 'true') {
      clearQuery()
      const nextAuthUri = getAzureOauthAuthUri() || authUri
      if (!isEmpty(nextAuthUri)) {
        openSignInWindow('AZURE', nextAuthUri, receiveMessage)
      } else {
        clearAzureOauthSession()
      }
      return
    }

    if (!isEmpty(code) && !isEmpty(state) && !isEmpty(sessionState)) {
      const callbackKey = `${code}:${state}`
      if (!tryClaimAzureOauthCallback(callbackKey)) {
        return
      }

      const connect = async () => {
        try {
          const result = await submitAzureConnection(code, state, sessionState)
          if (result.error) {
            releaseAzureOauthCallback(callbackKey)
            return
          }
          completeAzureOauthCallback(callbackKey)
          clearAzureOauthSession()
        } catch {
          releaseAzureOauthCallback(callbackKey)
        }
      }
      connect()
    }
  }, [
    code,
    state,
    sessionState,
    adminConsent,
    consentError,
    consentErrorDescription,
    enabled,
    accountId,
    submitAzureConnection,
    navigate,
    pathname,
    authUri,
    receiveMessage,
    dispatch,
  ])

  const handleClickToConnectAzure = async () => {
    if (!accountId) {
      return
    }
    setAzureOauthAccountId(accountId)
    clearAzureOauthAuthUri()
    // Open blank popup synchronously so Safari/Firefox keep the user-gesture
    // chain; navigate after initiate returns the auth URI.
    const popup = openSignInWindow('AZURE', 'about:blank', receiveMessage)
    const oauthUrls = await dispatch(
      initiateAzureOAuthFlow(productType, accountId, !!accountIdFromParams)
    )
    const consentOrAuthUri = pickConsentOrAuthUri(oauthUrls)
    if (oauthUrls?.authUri) {
      setAzureOauthAuthUri(oauthUrls.authUri)
    }
    if (
      !consentOrAuthUri ||
      isEmpty(consentOrAuthUri) ||
      !popup ||
      popup.closed
    ) {
      clearAzureOauthSession()
      closeSignInWindow()
      return
    }
    openSignInWindow('AZURE', consentOrAuthUri, receiveMessage)
  }

  return (
    <ButtonV2 onClick={handleClickToConnectAzure} disabled={!enabled}>
      Connect Microsoft Partner Center
    </ButtonV2>
  )
}
